






























































import { Vue, Component } from 'vue-property-decorator';
import { Typography } from '@/components/atoms';
import { ContentContainer, FilledButton } from '@/components/molecules';
import CampaignCard from '@/componentsV2/molecules/CampaignCard/CampaignCard.vue';
import EmptyStateList from '@/components/organisms/OfferFreeCoins/EmptyStateList.vue';
import CampaignListItem from '@/components/organisms/OfferFreeCoins/CampaignListItem.vue';
import PlusIcon from '@/components/icons/Plus.vue';

import OfferFreeCoinsService from '@/api/OfferFreeCoinsService';
import { treatError } from '@/utils';
import eventBus from '@/utils/eventBus';
import { Campaign } from '@/models/marketingCenter/freeCoins';

import dayjs from 'dayjs';
import BillingNetworkTag from '@/componentsV2/atoms/BillingNetworkTag/BillingNetworkTag.vue';
import PerformanceItem from '@/components/molecules/PerformanceItem/PerformanceItem.vue';
import PerformanceList from '@/components/molecules/PerformanceList/PerformanceList.vue';

@Component({
  components: {
    Typography,
    ContentContainer,
    FilledButton,
    EmptyStateList,
    CampaignListItem,
    PlusIcon,
    CampaignCard,
    BillingNetworkTag,
    PerformanceItem,
    PerformanceList
  }
})
export default class OfferFreeCoinsOffersList extends Vue {
  freeCoinsService: OfferFreeCoinsService = new OfferFreeCoinsService();
  loading: boolean = true;
  campaigns: Campaign[] = [];
  totalCount: number = 0;
  nextPage: string = '';

  observer: IntersectionObserver | null = null;
  triggerIntersected = false;

  overviewData: Record<string, number> = {};

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get title() {
    return 'Welcome Coins';
  }

  get showEmptyState() {
    return !this.loading && !this.campaigns.length;
  }

  get billingStatus() {
    return this.business.subscriptionStatus;
  }

  get business() {
    return this.$store.getters['business/getBusinessInfo'].business || {};
  }

  get totalCampaigns() {
    return `${this.totalCount} ${this.$tc(
      `common.pluralize.campaign`,
      this.totalCount || 1
    )} total`;
  }

  get totalValues() {
    return {
      coinsClaimed: {
        counter: this.overviewData.coinsClaimed || 0,
        label: 'Total Coins Claimed',
        description:
          'Total amount of Welcome Coins claimed by existing and new members.'
      },
      membersWhoClaimed: {
        counter: this.overviewData.membersWhoClaimed || 0,
        label: 'Total Members Who Claimed',
        description: 'Total number of members that claimed Welcome Coins.'
      }
    };
  }

  async getOffersList() {
    try {
      this.loading = true;
      this.$showLoaderWithLocker();
      const filterOfPeriod = {
        sort: 'firstcoin_campaign.createdAt:DESC',
        initialDateFreecoins: dayjs()
          .subtract(5, 'days')
          .startOf('day')
          .toISOString(),
        stoppedDateFreecoins: dayjs()
          .endOf('day')
          .toISOString()
      };

      const response = await this.freeCoinsService.listOfferFreeCoins(
        this.nextPage,
        { params: filterOfPeriod }
      );

      this.campaigns = [...this.campaigns, ...response.data.items];
      this.totalCount = response.data.total;
      this.overviewData = {
        coinsClaimed: this.campaigns.reduce(
          (prev, curr) => prev + (curr.rewardedCoins || 0),
          0
        ),
        membersWhoClaimed: this.campaigns.reduce(
          (prev, curr) => prev + (curr.customersClaimed || 0),
          0
        )
      };
      this.nextPage = response.data.links.next;
    } catch (e) {
      treatError(e);
    } finally {
      this.loading = false;
      this.$hideLoader();
    }
  }

  async updateActiveCampaign({ id, status }: { id: string; status: boolean }) {
    try {
      this.$showLoaderWithLocker();

      const newStatus = status ? 'inactive' : 'active';

      const campaignToUpdate = this.campaigns.find(
        (campaign: Campaign) => campaign.id === id
      );

      if (!campaignToUpdate) {
        this.$notification('Failed to find the selected campaign');
        return;
      }

      const response = await this.freeCoinsService.updateOfferFreeCoin(id, {
        allowRepeatingCustomers: campaignToUpdate.allowRepeatingCustomers,
        limitType: campaignToUpdate.limitType,
        visibilityType: campaignToUpdate.visibilityType,
        customerLimit: campaignToUpdate.customerLimit,
        dateLimit: campaignToUpdate.dateLimit || undefined,
        status: newStatus
      });

      if (response.status !== 200) return;

      const updatedCampaigns = this.campaigns.map((campaign: Campaign) => {
        if (campaign.id === id) {
          return {
            ...campaign,
            status: newStatus
          };
        }
        return {
          ...campaign,
          status: 'inactive'
        };
      });

      const statusLabel = status ? 'deactivated' : 'activated';

      this.campaigns = updatedCampaigns;
      this.$notification(`Campaign successfully ${statusLabel}`);

      this.$analytics({
        eventCategory: 'Free Coins Campaign | Active/Inactive switch component',
        eventAction: 'click',
        eventLabel: `${statusLabel.charAt(0).toUpperCase() +
          statusLabel.slice(1)} campaign`
      });
    } catch (e) {
      treatError(e);
    } finally {
      this.$hideLoader();
    }
  }

  createObserver() {
    const target = this.$refs.target as HTMLElement;

    const observer = new IntersectionObserver(async entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.nextPage && this.nextPage.length) {
          this.getOffersList();
        }
      });
    });

    if (target) {
      observer.observe(target);
    }
  }

  gotoOfferCreation() {
    this.$router.push('/marketing-center/offer-free-coins/create');

    this.$analytics({
      eventCategory: 'Free Coins Campaign | Create new campaign button',
      eventAction: 'click',
      eventLabel: 'Clicked on Create new campaign button'
    });
  }

  get hasActiveCampaign() {
    return this.campaigns.find(
      (campaign: Campaign) => campaign.status === 'active'
    );
  }

  async created() {
    await this.getOffersList();
    this.$nextTick(function() {
      this.createObserver();
    });
  }

  updateCampaign(campaign: Campaign) {
    const updatedCampaigns = this.campaigns.map(currentCampaign => {
      if (campaign.id === currentCampaign.id) {
        return campaign;
      }

      return currentCampaign;
    });

    this.campaigns = updatedCampaigns;
  }

  mounted() {
    eventBus.$on('changeFreeCoinCampaignStatus', this.updateActiveCampaign);
    eventBus.$on('updateFreeCoinCampaignList', this.updateCampaign);
  }

  beforeDestroy() {
    eventBus.$off('changeFreeCoinCampaignStatus', this.updateActiveCampaign);
    eventBus.$off('updateFreeCoinCampaignList', this.updateCampaign);
  }
}
